import { Button, Title } from "@components";
import { useAnalyticsReport } from "shared/hooks/useAnalyticsReport";
import { useDragScroll } from "shared/hooks/useDragScroll";
import styles from "styles/analytics.module.scss";
import ReportFilters from "./Reports/ReportFilters";
import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { EnvironmentStorage } from "shared/tools/EnvironmentStorage";
import { UserStorage } from "shared/tools/UserStorage";
import { EnvironmentsAPI } from "providers/EnvironmentsAPI";

const EVENTS = [
  { key: "ACCESS", label: "ACESSOS" },
  { key: "PRODUCT_VIEW", label: "PRODUTO" },
  { key: "ORDER_FINISHED", label: "FECHOU PEDIDO" },
  { key: "LOGIN", label: "LOGIN" },
  { key: "REGISTER", label: "CADASTRO" },
  { key: "ENDERECO_CABECALHO", label: "ENDERECO_CABECALHO" },
  { key: "ENDERECO_CHAMADA_ACEITO", label: "ENDERECO_CHAMADA_ACEITO" },
  { key: "ENDERECO_CHAMADA_NEGADO", label: "ENDERECO_CHAMADA_NEGADO" },
  { key: "MODAL_ENDERECO", label: "MODAL_ENDERECO" },
  { key: "MODAL_ENDERECO_DELIVERY", label: "MODAL_ENDERECO_DELIVERY" },
  { key: "MODAL_ENDERECO_RETIRADA", label: "MODAL_ENDERECO_RETIRADA" },
  {
    key: "MODAL_ENDERECO_COMER_NO_LOCAL",
    label: "MODAL_ENDERECO_COMER_NO_LOCAL",
  },
  { key: "PERMITIU_LOCALIZACAO", label: "PERMITIU_LOCALIZACAO" },
  { key: "NEGOU_LOCALIZACAO", label: "NEGOU_LOCALIZACAO" },
  { key: "LOCALIZACAO_ATUAL", label: "LOCALIZACAO_ATUAL" },
  { key: "OUTRA_LOCALIZACAO", label: "OUTRA_LOCALIZACAO" },
  { key: "CONCLUIU_OUTRA_LOCALIZACAO", label: "CONCLUIU_OUTRA_LOCALIZACAO" },
  { key: "NEGOU_ENDERECO_INFORMOU", label: "NEGOU_ENDERECO_INFORMOU" },
  { key: "NEGOU_ENDERECO_CONTINUOU", label: "NEGOU_ENDERECO_CONTINUOU" },
  { key: "ENDERECO_LISTAGEM_UNIDADES", label: "ENDERECO_LISTAGEM_UNIDADES" },
  {
    key: "NEGOU_ENDERECO_INFORMOU_CONCLUIU",
    label: "NEGOU_ENDERECO_INFORMOU_CONCLUIU",
  },
];

const normalizeTableData = (rawData) => {
  const rows = [];

  Object.entries(rawData).forEach(([brand, dates]) => {
    Object.entries(dates).forEach(([date, events]) => {
      rows.push({
        brand,
        date,
        events: events || {},
      });
    });
  });

  return rows;
};

const MIN_DATE = dayjs("2026-01-21");

const Analytics2 = () => {
  const [filters, setFilters] = useState({});
  const [dateFilters, setDateFilters] = useState({});
  const [openDateFilters, setOpenDateFilters] = useState(false);
  const [envName, setEnvName] = useState("");

  const filtersRef = useRef(null);

  const safeStartDate = useMemo(() => {
    if (!filters?.start_date) return MIN_DATE;

    const start = new Date(filters.start_date);
    return start < MIN_DATE ? MIN_DATE : start;
  }, [filters?.start_date]);

  const today = useMemo(() => dayjs().format("YYYY-MM-DD"), []);

  const { data } = useAnalyticsReport({
    startDate: safeStartDate,
    endDate: filters?.end_date || today,
  });

  const rows = normalizeTableData(data);

  const normalizeBrandName = (brand) => {
    return brand.replace(/\s*\(\d+\)\s*$/, "");
  };

  useEffect(() => {
    const loadData = async () => {
      let { environment_id, user_type } = UserStorage.getLoggedUser();

      if (user_type === "BRAND") {
        const holdingResponse = await EnvironmentsAPI.getById(environment_id);
        environment_id = holdingResponse.getData({}).environment_id;
      }

      const response = await EnvironmentsAPI.getUnits(String(environment_id));
      const data = response.getData({});

      const holdingName = data?.name;
      if (holdingName) setEnvName(holdingName);
      else {
        const environment = EnvironmentStorage.getEnvironment();

        const envName =
          environment?.type === "UNIT"
            ? environment?.brand?.name
            : environment?.name;

        setEnvName(envName);
      }
    };

    loadData();
  }, []);

  const getDailyEventTotals = (rows, targetBrandName) => {
    return rows.reduce((acc, row) => {
      const cleanBrand = normalizeBrandName(row.brand);

      if (!cleanBrand.includes(targetBrandName)) return acc;

      if (!acc[row.date]) {
        acc[row.date] = {};
      }

      Object.entries(row.events || {}).forEach(([event, count]) => {
        acc[row.date][event] = (acc[row.date][event] || 0) + count;
      });

      return acc;
    }, {});
  };

  const dailyTotals = getDailyEventTotals(rows, envName);

  const getTotals = (rows) => {
    return rows.reduce((acc, row) => {
      Object.entries(row || {}).forEach(([event, count]) => {
        acc[event] = (acc[event] || 0) + count;
      });
      return acc;
    }, {});
  };

  const totals = getTotals(Object.values(dailyTotals));

  const Table = ({ brand, rows }) => {
    const { scrollRef, onMouseDown } = useDragScroll();

    return (
      <div className={styles.box2} key={brand}>
        <div className={styles.container}>
          <div
            onMouseDown={onMouseDown}
            ref={scrollRef}
            className={styles.table}
          >
            <table>
              <thead>
                <tr>
                  <th
                    style={{
                      position: "sticky",
                      left: 0,
                      zIndex: 200,
                      backgroundColor: "#1F1F1F",
                      minWidth: 220,
                    }}
                  >
                    {normalizeBrandName(brand || "")}
                  </th>

                  {EVENTS.map((event) => (
                    <th key={event.key}>{event.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(rows)
                  .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
                  .map(([date, events]) => (
                    <tr key={date}>
                      <td
                        style={{
                          position: "sticky",
                          left: 0,
                          fontWeight: "bold",
                        }}
                      >
                        {dayjs(date).format("DD/MM/YYYY")}
                      </td>

                      {EVENTS.map((event) => (
                        <td key={event.key} style={{ textAlign: "center" }}>
                          {events?.[event.key] || 0}
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
              <tfoot>
                <tr>
                  <td
                    style={{
                      position: "sticky",
                      left: 0,
                    }}
                  ></td>
                  {EVENTS.map((event) => {
                    return (
                      <td key={event.key} style={{ textAlign: "center" }}>
                        {totals?.[event.key] || 0}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Title title="Analytics">
        <Button onClick={() => setOpenDateFilters(true)}>Filtrar</Button>
      </Title>

      <Table brand={envName} rows={dailyTotals} />

      <ReportFilters
        ref={filtersRef}
        open={openDateFilters}
        onClose={() => {
          setOpenDateFilters(false);
        }}
        dateFiltersProp={dateFilters}
        onUpdateFilters={(filters) => {
          setFilters(filters);
        }}
        hideOrigins
        hideUnits
        hideBrands
        hideHours={true}
        onUpdateDateFilters={(filters) => {
          setDateFilters(filters);
        }}
      />
    </>
  );
};

export default Analytics2;
